import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { format } from 'date-fns';
import axios from 'axios';
import { 
  User, 
  Briefcase, 
  Mail, 
  Phone,
  Users,
  MapPin,
  CalendarIcon,
  Clock,
  Plane,
  Car,
  Luggage,
  MessageSquare,
  Shield,
  CheckCircle,
  ChevronRight,
  ChevronLeft,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IMAGES = {
  escalade: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/g0b5gai9_IMG_7208.jpg',
};

const tripTypes = [
  { value: 'airport_transfer', label: 'Airport Transfer' },
  { value: 'hourly', label: 'Hourly Chauffeur Service' },
  { value: 'city_to_city', label: 'City to City Transportation' },
  { value: 'event', label: 'Event Transportation' },
];

const airports = [
  { value: 'IAD', label: 'Washington Dulles International Airport (IAD)' },
  { value: 'DCA', label: 'Ronald Reagan Washington National Airport (DCA)' },
  { value: 'BWI', label: 'Baltimore/Washington International Airport (BWI)' },
];

const vehicleTypes = [
  { value: 'sedan', label: 'Luxury Sedan', description: 'Mercedes S-Class or similar' },
  { value: 'suv', label: 'Executive SUV', description: 'Cadillac Escalade or similar' },
  { value: 'sprinter', label: 'Sprinter Van', description: 'Group transportation' },
];

const luggageOptions = [
  { value: 'light', label: 'Light', description: '1-2 small bags' },
  { value: 'standard', label: 'Standard', description: '2-3 regular bags' },
  { value: 'large', label: 'Large / Multiple', description: '4+ bags or oversized' },
];

const steps = [
  { id: 1, title: 'Client Info', icon: User },
  { id: 2, title: 'Trip Details', icon: MapPin },
  { id: 3, title: 'Vehicle', icon: Car },
  { id: 4, title: 'Review', icon: CheckCircle },
];

export default function BookRidePage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    // Client Info
    full_name: '',
    company: '',
    email: '',
    phone: '',
    passengers: '1',
    
    // Trip Details
    trip_type: '',
    pickup_location: '',
    dropoff_location: '',
    pickup_date: null,
    pickup_time: '',
    return_trip: false,
    return_date: null,
    return_time: '',
    
    // Airport Info
    airport: '',
    flight_number: '',
    flight_type: '',
    
    // Vehicle
    vehicle_type: '',
    luggage_amount: '',
    
    // Special Requests
    special_requests: ''
  });

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.full_name && formData.email && formData.phone;
      case 2:
        return formData.trip_type && formData.pickup_location && 
               formData.dropoff_location && formData.pickup_date && formData.pickup_time;
      case 3:
        return formData.vehicle_type && formData.luggage_amount;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    } else {
      toast.error('Please fill in all required fields');
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(1) || !validateStep(2) || !validateStep(3)) {
      toast.error('Please complete all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        passengers: parseInt(formData.passengers),
        pickup_date: formData.pickup_date ? format(formData.pickup_date, 'yyyy-MM-dd') : '',
        return_date: formData.return_date ? format(formData.return_date, 'yyyy-MM-dd') : null,
      };

      await axios.post(`${API}/bookings`, payload);
      
      setIsSubmitted(true);
      toast.success('Reservation request submitted successfully!');
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to submit reservation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div data-testid="booking-success" className="min-h-screen bg-[#050505] pt-24">
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <div className="w-20 h-20 mx-auto mb-8 flex items-center justify-center bg-[#D4AF37]/10 border border-[#D4AF37]">
            <CheckCircle className="w-10 h-10 text-[#D4AF37]" />
          </div>
          <h1 className="text-4xl font-serif text-white mb-4">
            Reservation Request Received
          </h1>
          <p className="text-[#A3A3A3] text-lg mb-8">
            Thank you for choosing Capital Chauffeur Services. A member of our team 
            will confirm your reservation shortly.
          </p>
          <div className="p-6 bg-[#0A0A0A] border border-white/10 mb-8">
            <p className="text-white mb-2">Questions? Contact us directly:</p>
            <a href="tel:+12026840534" className="text-[#D4AF37] text-xl">
              +1 (202) 684-0534
            </a>
          </div>
          <Button
            data-testid="book-another-btn"
            onClick={() => {
              setIsSubmitted(false);
              setCurrentStep(1);
              setFormData({
                full_name: '', company: '', email: '', phone: '', passengers: '1',
                trip_type: '', pickup_location: '', dropoff_location: '',
                pickup_date: null, pickup_time: '', return_trip: false,
                return_date: null, return_time: '', airport: '', flight_number: '',
                flight_type: '', vehicle_type: '', luggage_amount: '', special_requests: ''
              });
            }}
            className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-8 py-6"
          >
            Book Another Ride
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="book-ride-page" className="min-h-screen bg-[#050505]">
      {/* Hero */}
      <section className="relative pt-32 pb-12">
        <div className="absolute inset-0">
          <img src={IMAGES.escalade} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/90 to-[#050505]"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4 block">
            Book Your Ride
          </span>
          <h1 data-testid="book-headline" className="text-4xl lg:text-5xl font-serif text-white mb-4">
            Request a Reservation
          </h1>
          <p className="text-[#A3A3A3] text-lg max-w-xl">
            Complete the form below and a member of our team will confirm your 
            reservation shortly.
          </p>
          
          {/* Contact Info Bar */}
          <div className="mt-8 flex flex-wrap gap-6">
            <a href="tel:+12026840534" className="flex items-center gap-2 text-white hover:text-[#D4AF37] transition-colors">
              <Phone className="w-4 h-4 text-[#D4AF37]" />
              <span>+1 (202) 684-0534</span>
            </a>
            <a href="mailto:contact@capitalchauffeurservices.com" className="flex items-center gap-2 text-white hover:text-[#D4AF37] transition-colors">
              <Mail className="w-4 h-4 text-[#D4AF37]" />
              <span>contact@capitalchauffeurservices.com</span>
            </a>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="pb-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          {/* Progress Steps */}
          <div className="mb-12">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <button
                    onClick={() => step.id < currentStep && setCurrentStep(step.id)}
                    disabled={step.id > currentStep}
                    className={`flex flex-col items-center gap-2 ${
                      step.id <= currentStep ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                    }`}
                  >
                    <div className={`w-12 h-12 flex items-center justify-center border transition-colors ${
                      step.id < currentStep 
                        ? 'bg-[#D4AF37] border-[#D4AF37] text-black' 
                        : step.id === currentStep 
                          ? 'border-[#D4AF37] text-[#D4AF37]' 
                          : 'border-white/20 text-white/40'
                    }`}>
                      {step.id < currentStep ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <step.icon className="w-5 h-5" />
                      )}
                    </div>
                    <span className={`text-xs tracking-wider uppercase hidden sm:block ${
                      step.id <= currentStep ? 'text-white' : 'text-white/40'
                    }`}>
                      {step.title}
                    </span>
                  </button>
                  {index < steps.length - 1 && (
                    <div className={`w-12 sm:w-24 h-px mx-2 ${
                      step.id < currentStep ? 'bg-[#D4AF37]' : 'bg-white/10'
                    }`}></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Form Card */}
          <div className="glass p-8 lg:p-12">
            {/* Step 1: Client Information */}
            {currentStep === 1 && (
              <div data-testid="step-1" className="space-y-8 fade-in">
                <div>
                  <h2 className="text-2xl font-serif text-white mb-2">Client Information</h2>
                  <p className="text-[#A3A3A3]">Tell us about yourself</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-white flex items-center gap-2">
                      <User className="w-4 h-4 text-[#D4AF37]" />
                      Full Name *
                    </Label>
                    <Input
                      data-testid="input-full-name"
                      value={formData.full_name}
                      onChange={(e) => updateFormData('full_name', e.target.value)}
                      placeholder="John Smith"
                      className="bg-transparent border-white/20 focus:border-[#D4AF37] text-white placeholder:text-white/30"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-[#D4AF37]" />
                      Company / Organization
                    </Label>
                    <Input
                      data-testid="input-company"
                      value={formData.company}
                      onChange={(e) => updateFormData('company', e.target.value)}
                      placeholder="Optional"
                      className="bg-transparent border-white/20 focus:border-[#D4AF37] text-white placeholder:text-white/30"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white flex items-center gap-2">
                      <Mail className="w-4 h-4 text-[#D4AF37]" />
                      Email Address *
                    </Label>
                    <Input
                      data-testid="input-email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateFormData('email', e.target.value)}
                      placeholder="john@company.com"
                      className="bg-transparent border-white/20 focus:border-[#D4AF37] text-white placeholder:text-white/30"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white flex items-center gap-2">
                      <Phone className="w-4 h-4 text-[#D4AF37]" />
                      Phone Number *
                    </Label>
                    <Input
                      data-testid="input-phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => updateFormData('phone', e.target.value)}
                      placeholder="+1 (202) 555-0123"
                      className="bg-transparent border-white/20 focus:border-[#D4AF37] text-white placeholder:text-white/30"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#D4AF37]" />
                      Number of Passengers
                    </Label>
                    <Select value={formData.passengers} onValueChange={(v) => updateFormData('passengers', v)}>
                      <SelectTrigger data-testid="select-passengers" className="bg-transparent border-white/20 text-white">
                        <SelectValue placeholder="Select passengers" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/10">
                        {[1,2,3,4,5,6,7,8,9,10].map(n => (
                          <SelectItem key={n} value={String(n)} className="text-white hover:bg-white/10">
                            {n} {n === 1 ? 'Passenger' : 'Passengers'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Trip Details */}
            {currentStep === 2 && (
              <div data-testid="step-2" className="space-y-8 fade-in">
                <div>
                  <h2 className="text-2xl font-serif text-white mb-2">Trip Details</h2>
                  <p className="text-[#A3A3A3]">Where and when do you need transportation?</p>
                </div>

                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label className="text-white">Trip Type *</Label>
                    <Select value={formData.trip_type} onValueChange={(v) => updateFormData('trip_type', v)}>
                      <SelectTrigger data-testid="select-trip-type" className="bg-transparent border-white/20 text-white">
                        <SelectValue placeholder="Select trip type" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/10">
                        {tripTypes.map(type => (
                          <SelectItem key={type.value} value={type.value} className="text-white hover:bg-white/10">
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-white flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-[#D4AF37]" />
                        Pickup Location *
                      </Label>
                      <Input
                        data-testid="input-pickup"
                        value={formData.pickup_location}
                        onChange={(e) => updateFormData('pickup_location', e.target.value)}
                        placeholder="Address or location"
                        className="bg-transparent border-white/20 focus:border-[#D4AF37] text-white placeholder:text-white/30"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-white flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-[#D4AF37]" />
                        Drop-off Location *
                      </Label>
                      <Input
                        data-testid="input-dropoff"
                        value={formData.dropoff_location}
                        onChange={(e) => updateFormData('dropoff_location', e.target.value)}
                        placeholder="Address or location"
                        className="bg-transparent border-white/20 focus:border-[#D4AF37] text-white placeholder:text-white/30"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-white flex items-center gap-2">
                        <CalendarIcon className="w-4 h-4 text-[#D4AF37]" />
                        Pickup Date *
                      </Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            data-testid="btn-pickup-date"
                            variant="outline"
                            className="w-full justify-start text-left bg-transparent border-white/20 text-white hover:bg-white/5"
                          >
                            {formData.pickup_date ? format(formData.pickup_date, 'PPP') : 'Select date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-[#0A0A0A] border-white/10">
                          <Calendar
                            mode="single"
                            selected={formData.pickup_date}
                            onSelect={(d) => updateFormData('pickup_date', d)}
                            disabled={(date) => date < new Date()}
                            className="bg-[#0A0A0A]"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-white flex items-center gap-2">
                        <Clock className="w-4 h-4 text-[#D4AF37]" />
                        Pickup Time *
                      </Label>
                      <Input
                        data-testid="input-pickup-time"
                        type="time"
                        value={formData.pickup_time}
                        onChange={(e) => updateFormData('pickup_time', e.target.value)}
                        className="bg-transparent border-white/20 focus:border-[#D4AF37] text-white"
                      />
                    </div>
                  </div>

                  {/* Return Trip */}
                  <div className="p-4 bg-white/5 border border-white/10">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="return-trip"
                        data-testid="checkbox-return"
                        checked={formData.return_trip}
                        onCheckedChange={(checked) => updateFormData('return_trip', checked)}
                        className="border-white/20 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                      />
                      <Label htmlFor="return-trip" className="text-white cursor-pointer">
                        I need a return trip
                      </Label>
                    </div>

                    {formData.return_trip && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                        <div className="space-y-2">
                          <Label className="text-white">Return Date</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                data-testid="btn-return-date"
                                variant="outline"
                                className="w-full justify-start text-left bg-transparent border-white/20 text-white hover:bg-white/5"
                              >
                                {formData.return_date ? format(formData.return_date, 'PPP') : 'Select date'}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 bg-[#0A0A0A] border-white/10">
                              <Calendar
                                mode="single"
                                selected={formData.return_date}
                                onSelect={(d) => updateFormData('return_date', d)}
                                disabled={(date) => date < new Date()}
                                className="bg-[#0A0A0A]"
                              />
                            </PopoverContent>
                          </Popover>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-white">Return Time</Label>
                          <Input
                            data-testid="input-return-time"
                            type="time"
                            value={formData.return_time}
                            onChange={(e) => updateFormData('return_time', e.target.value)}
                            className="bg-transparent border-white/20 focus:border-[#D4AF37] text-white"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Airport Info - Only for Airport Transfers */}
                  {formData.trip_type === 'airport_transfer' && (
                    <div className="p-6 bg-[#0A0A0A] border border-[#D4AF37]/20 space-y-6">
                      <div className="flex items-center gap-3">
                        <Plane className="w-5 h-5 text-[#D4AF37]" />
                        <h3 className="text-lg font-serif text-white">Airport Information</h3>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-white">Airport *</Label>
                        <Select value={formData.airport} onValueChange={(v) => updateFormData('airport', v)}>
                          <SelectTrigger data-testid="select-airport" className="bg-transparent border-white/20 text-white">
                            <SelectValue placeholder="Select airport" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A0A] border-white/10">
                            {airports.map(airport => (
                              <SelectItem key={airport.value} value={airport.value} className="text-white hover:bg-white/10">
                                {airport.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label className="text-white">Flight Number</Label>
                          <Input
                            data-testid="input-flight-number"
                            value={formData.flight_number}
                            onChange={(e) => updateFormData('flight_number', e.target.value)}
                            placeholder="e.g., UA1234"
                            className="bg-transparent border-white/20 focus:border-[#D4AF37] text-white placeholder:text-white/30"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-white">Flight Type</Label>
                          <RadioGroup
                            value={formData.flight_type}
                            onValueChange={(v) => updateFormData('flight_type', v)}
                            className="flex gap-6"
                          >
                            <div className="flex items-center gap-2">
                              <RadioGroupItem value="arrival" id="arrival" className="border-white/20 text-[#D4AF37]" />
                              <Label htmlFor="arrival" className="text-white cursor-pointer">Arrival</Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <RadioGroupItem value="departure" id="departure" className="border-white/20 text-[#D4AF37]" />
                              <Label htmlFor="departure" className="text-white cursor-pointer">Departure</Label>
                            </div>
                          </RadioGroup>
                        </div>
                      </div>

                      <p className="text-[#A3A3A3] text-sm flex items-center gap-2">
                        <Shield className="w-4 h-4 text-[#D4AF37]" />
                        We monitor all flights to ensure timely pickup.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Vehicle & Requests */}
            {currentStep === 3 && (
              <div data-testid="step-3" className="space-y-8 fade-in">
                <div>
                  <h2 className="text-2xl font-serif text-white mb-2">Vehicle Preference</h2>
                  <p className="text-[#A3A3A3]">Select your vehicle and luggage requirements</p>
                </div>

                {/* Vehicle Type */}
                <div className="space-y-4">
                  <Label className="text-white flex items-center gap-2">
                    <Car className="w-4 h-4 text-[#D4AF37]" />
                    Vehicle Type *
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {vehicleTypes.map((vehicle) => (
                      <button
                        key={vehicle.value}
                        data-testid={`vehicle-${vehicle.value}`}
                        type="button"
                        onClick={() => updateFormData('vehicle_type', vehicle.value)}
                        className={`p-6 text-left border transition-colors ${
                          formData.vehicle_type === vehicle.value
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                            : 'border-white/10 hover:border-white/30'
                        }`}
                      >
                        <h4 className="text-white font-medium mb-1">{vehicle.label}</h4>
                        <p className="text-[#A3A3A3] text-sm">{vehicle.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Luggage Amount */}
                <div className="space-y-4">
                  <Label className="text-white flex items-center gap-2">
                    <Luggage className="w-4 h-4 text-[#D4AF37]" />
                    Luggage Amount *
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {luggageOptions.map((option) => (
                      <button
                        key={option.value}
                        data-testid={`luggage-${option.value}`}
                        type="button"
                        onClick={() => updateFormData('luggage_amount', option.value)}
                        className={`p-6 text-left border transition-colors ${
                          formData.luggage_amount === option.value
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                            : 'border-white/10 hover:border-white/30'
                        }`}
                      >
                        <h4 className="text-white font-medium mb-1">{option.label}</h4>
                        <p className="text-[#A3A3A3] text-sm">{option.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Special Requests */}
                <div className="space-y-2">
                  <Label className="text-white flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-[#D4AF37]" />
                    Special Requests
                  </Label>
                  <Textarea
                    data-testid="input-special-requests"
                    value={formData.special_requests}
                    onChange={(e) => updateFormData('special_requests', e.target.value)}
                    placeholder="Security requirements, child seat requests, multiple stops, VIP coordination, etc."
                    className="bg-transparent border-white/20 focus:border-[#D4AF37] text-white placeholder:text-white/30 min-h-[120px]"
                  />
                </div>

                {/* Privacy Notice */}
                <div className="p-4 bg-white/5 border border-white/10">
                  <p className="text-[#A3A3A3] text-sm flex items-start gap-3">
                    <Shield className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    Your information is kept private and used only to coordinate your 
                    transportation service.
                  </p>
                </div>
              </div>
            )}

            {/* Step 4: Review */}
            {currentStep === 4 && (
              <div data-testid="step-4" className="space-y-8 fade-in">
                <div>
                  <h2 className="text-2xl font-serif text-white mb-2">Review Your Reservation</h2>
                  <p className="text-[#A3A3A3]">Please verify all details before submitting</p>
                </div>

                <div className="space-y-6">
                  {/* Client Info Summary */}
                  <div className="p-6 bg-[#0A0A0A] border border-white/10">
                    <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                      <User className="w-4 h-4 text-[#D4AF37]" />
                      Client Information
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-[#A3A3A3]">Name:</span>
                        <span className="text-white ml-2">{formData.full_name}</span>
                      </div>
                      <div>
                        <span className="text-[#A3A3A3]">Email:</span>
                        <span className="text-white ml-2">{formData.email}</span>
                      </div>
                      <div>
                        <span className="text-[#A3A3A3]">Phone:</span>
                        <span className="text-white ml-2">{formData.phone}</span>
                      </div>
                      <div>
                        <span className="text-[#A3A3A3]">Passengers:</span>
                        <span className="text-white ml-2">{formData.passengers}</span>
                      </div>
                      {formData.company && (
                        <div>
                          <span className="text-[#A3A3A3]">Company:</span>
                          <span className="text-white ml-2">{formData.company}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Trip Details Summary */}
                  <div className="p-6 bg-[#0A0A0A] border border-white/10">
                    <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#D4AF37]" />
                      Trip Details
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-[#A3A3A3]">Trip Type:</span>
                        <span className="text-white">
                          {tripTypes.find(t => t.value === formData.trip_type)?.label}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#A3A3A3]">Pickup:</span>
                        <span className="text-white">{formData.pickup_location}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#A3A3A3]">Drop-off:</span>
                        <span className="text-white">{formData.dropoff_location}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#A3A3A3]">Date & Time:</span>
                        <span className="text-white">
                          {formData.pickup_date && format(formData.pickup_date, 'PPP')} at {formData.pickup_time}
                        </span>
                      </div>
                      {formData.return_trip && (
                        <div className="flex justify-between">
                          <span className="text-[#A3A3A3]">Return:</span>
                          <span className="text-white">
                            {formData.return_date && format(formData.return_date, 'PPP')} at {formData.return_time}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Vehicle Summary */}
                  <div className="p-6 bg-[#0A0A0A] border border-white/10">
                    <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                      <Car className="w-4 h-4 text-[#D4AF37]" />
                      Vehicle & Preferences
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-[#A3A3A3]">Vehicle:</span>
                        <span className="text-white">
                          {vehicleTypes.find(v => v.value === formData.vehicle_type)?.label}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#A3A3A3]">Luggage:</span>
                        <span className="text-white">
                          {luggageOptions.find(l => l.value === formData.luggage_amount)?.label}
                        </span>
                      </div>
                      {formData.special_requests && (
                        <div>
                          <span className="text-[#A3A3A3] block mb-2">Special Requests:</span>
                          <span className="text-white">{formData.special_requests}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-10 pt-8 border-t border-white/10">
              {currentStep > 1 ? (
                <Button
                  data-testid="btn-prev-step"
                  type="button"
                  onClick={prevStep}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5 rounded-none px-6 py-5"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
              ) : (
                <div></div>
              )}

              {currentStep < 4 ? (
                <Button
                  data-testid="btn-next-step"
                  type="button"
                  onClick={nextStep}
                  className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-6 py-5"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  data-testid="btn-submit-booking"
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-8 py-5 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Request Reservation'
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Below form text */}
          <p className="text-center text-[#A3A3A3] text-sm mt-8">
            A member of our team will confirm your reservation shortly.
          </p>
        </div>
      </section>
    </div>
  );
}
